/**
 * PageBackground — set the `<body>` background so it fills the whole
 * browser window, independent of the Phaser canvas size.
 *
 * Why: Phaser's FIT scale mode letterboxes the canvas on desktop (where
 * browser windows come in many aspect ratios). If the menu background
 * is a Phaser Image inside the scene, it gets clipped to the canvas
 * and the user sees dark bars at the edges. Setting the background
 * on `<body>` instead lets the image cover the full window while the
 * canvas (with its fixed design resolution) floats on top with UI
 * elements that never stretch.
 *
 * Coexistence with the camera passthrough: AR games shouldn't normally
 * use a page background — the camera feed is the background. Shell
 * scenes (MainMenu, Lobby, About, Settings, GameOver) call
 * `PageBackground.set()` when they mount and `PageBackground.clear()`
 * has no automatic teardown hook because the next shell scene will
 * call `set()` again with its own image.
 *
 * Resolves texture URLs from the Phaser texture cache so callers can
 * pass the same cache key they use with `SceneBackground.attach()`.
 */
export class PageBackground {
  /**
   * Set the body background to a scaled, cover-fit image.
   *
   * @param {object} opts
   * @param {string} [opts.url]       - explicit image URL
   * @param {Phaser.Scene} [opts.scene] - scene to resolve the cache key from
   * @param {string} [opts.key]       - Phaser texture cache key
   * @param {string} [opts.fallback='#12131a'] - color behind the image
   */
  static set(opts) {
    if (typeof document === 'undefined') return;

    let url = opts.url || null;
    if (!url && opts.scene && opts.key) {
      url = PageBackground.resolveUrl(opts.scene, opts.key);
    }

    const fallback = opts.fallback || '#12131a';
    const body = document.body;
    if (!body) return;

    if (url) {
      // CSS background shorthand: color url position / size repeat
      body.style.background = fallback + ' url("' + escapeCssUrl(url)
        + '") center center / cover no-repeat fixed';
    } else {
      body.style.background = fallback;
    }
  }

  /** Restore the body background to a flat color. */
  static clear(fallback) {
    if (typeof document === 'undefined') return;
    if (!document.body) return;
    document.body.style.background = fallback || '#12131a';
  }

  /**
   * Read the browser-resolved URL of a Phaser texture so we can use it as
   * a CSS `background-image`. Returns null if the texture isn't loaded or
   * the cache entry doesn't expose a source image (which happens for
   * RenderTextures, generated textures, etc.).
   */
  static resolveUrl(scene, key) {
    if (!scene || !scene.textures || !scene.textures.exists(key)) return null;
    const tex = scene.textures.get(key);
    // Walk the source list and grab the first one with a browser URL.
    const sources = tex.source || [];
    for (const src of sources) {
      const img = src && src.image;
      if (img && img.src) return img.src;
    }
    return null;
  }
}

/** Escape characters that would break out of a CSS url("...") literal. */
function escapeCssUrl(url) {
  return String(url).replace(/["\\]/g, '\\$&');
}
